import cn from 'classnames';

import { ContactCardFields } from 'src/types/components';

import DefaultBackingStar from 'assets/icons/contact-card-default.svg';
import EmailIcon from 'assets/icons/email.svg';
import PhoneIcon from 'assets/icons/phone.svg';

import Image from 'components/BaseHelpers/Image';
import Link from 'components/BaseHelpers/Link';
import Text from 'components/BaseHelpers/Text';

import styles from './ContactCard.module.scss';

type NameProps = {
  name: string;
  url: ContactCardFields['url'];
};

const Name = ({ name, url }: NameProps) => {
  if (url && url.href) {
    return (
      <>
        <Link
          {...url}
          className={styles.nameLink}
          aria-label={`View more details for ${name}.`}
        ></Link>
        <Text tag="p" field={name} className={styles.name} />
      </>
    );
  }

  return <Text tag="p" field={name} className={styles.name} />;
};

type FieldProps = {
  field?: string;
};

const PhoneField = ({ field }: FieldProps) => {
  if (!field) return null;

  return (
    <a href={`tel:${field}`} className={styles.contactField}>
      <PhoneIcon aria-hidden className={styles.fieldIcon} />
      <span className={styles.contactFieldLabel}>{field}</span>
    </a>
  );
};

const EmailField = ({ field }: FieldProps) => {
  if (!field) return null;

  const lineBrokenField = field.replaceAll('@', '<wbr>@');

  return (
    <a href={`mailto:${field}`} className={styles.contactField}>
      <EmailIcon aria-hidden className={styles.fieldIcon} />
      <div className={styles.contactFieldLabelWrapper}>
        <span
          className={styles.contactFieldLabel}
          dangerouslySetInnerHTML={{ __html: lineBrokenField }}
        />
      </div>
    </a>
  );
};

type Props = {
  className?: string;
  card: ContactCardFields;
};
const ContactCard = ({ card, className }: Props): JSX.Element | null => {
  const { person, url } = card;

  if (!person) {
    return null;
  }

  const { firstName, lastName, image, jobTitle, department, phone, email } = person;
  return (
    <li className={cn(styles.card, className)}>
      <div className={styles.imageWrapper}>
        {!image && <DefaultBackingStar className={styles.defaultImage} />}
        {image && <Image {...image} fill className={styles.image} />}
      </div>
      <div className={styles.contentWrapper}>
        <div className={styles.nameWrapper}>
          <Name name={`${firstName} ${lastName}`} url={url} />
        </div>
        {!!jobTitle && <Text tag="p" field={jobTitle} className={styles.jobField} />}
        {!!department && <Text tag="p" field={department} className={styles.jobField} />}
        <PhoneField field={phone} />
        <EmailField field={email} />
      </div>
    </li>
  );
};

export default ContactCard;
